import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    CircularProgress,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import axios from '../../services/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScoreIcon from '@mui/icons-material/Score';
import HistoryIcon from '@mui/icons-material/History';

interface DashboardStats {
    total_quizzes_attempted: number;
    total_quizzes_passed: number;
    average_score: number;
    recent_attempts: {
        quiz_title: string;
        score: number;
        date: string;
        passed: boolean;
    }[];
}

const initialStats: DashboardStats = {
    total_quizzes_attempted: 0,
    total_quizzes_passed: 0,
    average_score: 0,
    recent_attempts: [],
};

const StudentDashboard: React.FC = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState<DashboardStats>(initialStats);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get('/student/dashboard/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
            setError(t('student.loadError'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            {t('student.welcome', { name: user?.first_name })}
                        </Typography>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                        <Paper 
                            sx={{ 
                                p: 2, 
                                textAlign: 'center',
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <AssignmentIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('student.quizzesAttempted')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }}>
                                {stats.total_quizzes_attempted}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper 
                            sx={{ 
                                p: 2, 
                                textAlign: 'center',
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <CheckCircleIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('student.quizzesPassed')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }}>
                                {stats.total_quizzes_passed}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Paper 
                            sx={{ 
                                p: 2, 
                                textAlign: 'center',
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <ScoreIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('student.averageScore')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }}>
                                {stats.average_score.toFixed(1)}%
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper 
                            sx={{ 
                                p: 2,
                                bgcolor: '#2c3e50',
                                opacity: 0.9,
                                '&:hover': {
                                    opacity: 1,
                                    boxShadow: 6,
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <HistoryIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 0 }}>
                                    {t('student.recentAttempts')}
                                </Typography>
                            </Box>
                            {stats.recent_attempts.length > 0 ? (
                                <Box>
                                    {stats.recent_attempts.map((attempt, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                p: 2,
                                                mb: 1,
                                                border: 1,
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                                borderRadius: 1,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                bgcolor: 'white',
                                                '&:hover': {
                                                    boxShadow: 2,
                                                }
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ color: '#2c3e50' }}>
                                                    {attempt.quiz_title}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'rgba(44, 62, 80, 0.7)' }}>
                                                    {new Date(attempt.date).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{ color: attempt.passed ? '#4caf50' : '#f44336' }}
                                                >
                                                    {attempt.score}%
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: attempt.passed ? '#4caf50' : '#f44336' }}
                                                >
                                                    {attempt.passed ? t('student.passed') : t('student.failed')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body1" sx={{ color: 'white' }}>
                                    {t('student.noAttempts')}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default StudentDashboard;
